import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { Pencil } from "lucide-react";

import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useWillAnswer } from "@/shared/hooks/useWillAnswer";
import { useWillExtraction } from "@/shared/hooks/useWillExtraction";
import { formatContactName, isWillWitness } from "@/shared/utils/contactUtils";
import { getRelationshipLabel, RELATIONSHIP_OPTIONS, FAMILY_RELATIONSHIPS } from "@/shared/constants/relationshipOptions";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { PersonContact } from "@/shared/types/estateContact";
import type { WillWitness } from "@/shared/types/probateTypes";

interface WillOrganization {
  organization_name: string;
  amount?: number;
  amount_type?: string;
  description?: string;
}

function isFamily(contact: PersonContact): boolean {
  return !!contact.relationship && FAMILY_RELATIONSHIPS.has(contact.relationship.toLowerCase());
}

function getStatusLabel(status: string | null | undefined): string {
  if (status === "living") return "Living";
  if (status === "passed_away") return "Passed away";
  if (status === "not_sure") return "Not sure";
  return "";
}

function DotSeparator() {
  return <span className="size-1 shrink-0 rounded-full bg-stone-400" aria-hidden="true" />;
}

export const ReviewScreen: FC<FlowCustomComponentProps> = () => {
  const { person, user } = useAppContext();
  const {
    contacts,
    updateContactAsync,
    createContactAsync,
    deleteContact,
    isUpdating,
  isLoading: isContactsLoading,
  } = useUserContacts();
  const estateId = person?.estate_id;
  const hasWill = useWillAnswer(estateId);
  const { data: willExtraction } = useWillExtraction(estateId);
  const name = person?.first_name ?? "the decedent";
  const actingUserEmail = user?.email?.toLowerCase();

  const seededWitnessesRef = useRef(false);
  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;
  const createContactRef = useRef(createContactAsync);
  createContactRef.current = createContactAsync;
  useEffect(() => {
    if (seededWitnessesRef.current) return;
    if (isContactsLoading) return;
    const extractionWitnesses = (willExtraction?.specific?.witness_info ?? []) as WillWitness[];
    if (extractionWitnesses.length === 0) return;

    const existingNames = new Set(
      contactsRef.current
        .map((c) => formatContactName(c.first_name, c.last_name, c.middle_name).toLowerCase().trim()),
    );

    const missing = extractionWitnesses.filter(
      (w) => w.name && !existingNames.has(w.name.toLowerCase().trim()),
    );

    if (missing.length === 0) {
      seededWitnessesRef.current = true;
      return;
    }

    seededWitnessesRef.current = true;
    (async () => {
      try {
        await Promise.all(
          missing.map((witness) => {
            const parts = witness.name!.trim().split(/\s+/);
            return createContactRef.current({
              first_name: parts[0] || "",
              last_name: parts.slice(1).join(" ") || "",
              legal_role: ["witness"],
            });
          }),
        );
      } catch (err) {
        console.error("Failed to seed witness contacts:", err);
      }
    })();
  }, [willExtraction, isContactsLoading]);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editRelationship, setEditRelationship] = useState("");
  const [editStatus, setEditStatus] = useState("");

  const [addingSection, setAddingSection] = useState<"people" | "witness" | null>(null);
  const [addFirstName, setAddFirstName] = useState("");
  const [addLastName, setAddLastName] = useState("");
  const [addRelationship, setAddRelationship] = useState("");
  const [addStatus, setAddStatus] = useState("");

  const { people, witnesses } = useMemo(() => {
    const peopleList: PersonContact[] = [];
    const witnessList: PersonContact[] = [];
    const seenIds = new Set<number>();

    for (const contact of contacts) {
      if (seenIds.has(contact.id)) continue;
      seenIds.add(contact.id);
      if (actingUserEmail && contact.email?.toLowerCase() === actingUserEmail) continue;
      if (contact.is_hidden) continue;

      if (isWillWitness(contact) && !isFamily(contact)) {
        witnessList.push(contact);
      } else {
        peopleList.push(contact);
      }
    }

    return { people: peopleList, witnesses: witnessList };
  }, [contacts, actingUserEmail]);

  const organizations = useMemo((): WillOrganization[] => {
    const charitable = willExtraction?.specific?.charitable_contributions;
    if (!Array.isArray(charitable)) return [];
    return charitable.filter(
      (c: Record<string, unknown>) => c.organization_name,
    ) as WillOrganization[];
  }, [willExtraction]);

  const isNamedInWill = (contact: PersonContact) =>
    contact.extracted_from_document === true || !!contact.source_document_id;

  const isDualWitness = (contact: PersonContact) =>
    isWillWitness(contact) && isFamily(contact);

  const startEdit = (contact: PersonContact) => {
    setEditingId(contact.id);
    setEditFirstName(contact.first_name ?? "");
    setEditLastName(contact.last_name ?? "");
    setEditRelationship(contact.relationship ?? "");
    setEditStatus(contact.person_status ?? "");
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    await updateContactAsync({
      id: editingId,
      first_name: editFirstName,
      last_name: editLastName,
      relationship: editRelationship || null,
      person_status: editStatus || null,
    });
    setEditingId(null);
  };

  const handleRemove = (id: number) => {
    deleteContact(id, {
      onSuccess: () => setEditingId(null),
      onError: (err) => console.error("Failed to remove contact:", err),
    });
  };

  const startAdd = (section: "people" | "witness") => {
    setAddingSection(section);
    setAddFirstName("");
    setAddLastName("");
    setAddRelationship("");
    setAddStatus("");
  };

  const cancelAdd = () => {
    setAddingSection(null);
  };

  const saveAdd = async () => {
    if (!addFirstName.trim() || !addLastName.trim()) return;
    const contact: Record<string, unknown> = {
      first_name: addFirstName.trim(),
      last_name: addLastName.trim(),
    };
    if (addingSection === "witness") {
      contact.legal_role = ["witness"];
    } else {
      if (addRelationship) contact.relationship = addRelationship;
      if (addStatus) contact.person_status = addStatus;
    }
    await createContactAsync(contact);
    setAddingSection(null);
  };

  const renderEditRow = (contact: PersonContact, witnessMode = false) => (
    <div className="flex flex-col gap-3 bg-white p-4 border-t border-primary first:border-t-0">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>First name</Label>
          <Input value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Last name</Label>
          <Input value={editLastName} onChange={(e) => setEditLastName(e.target.value)} />
        </div>
      </div>
      {!witnessMode && (
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Relationship</Label>
            <Select value={editRelationship} onValueChange={setEditRelationship}>
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Status</Label>
            <Select value={editStatus} onValueChange={setEditStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="living">Living</SelectItem>
                <SelectItem value="passed_away">Passed away</SelectItem>
                <SelectItem value="not_sure">Not sure</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => handleRemove(contact.id)}
          className="text-sm font-medium text-destructive hover:underline"
        >
          Remove
        </button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={cancelEdit}>Cancel</Button>
          <Button variant="brand" size="sm" onClick={saveEdit} disabled={isUpdating}>Save</Button>
        </div>
      </div>
    </div>
  );

  const renderAddRow = (isWitnessSection: boolean) => (
    <div className="flex flex-col gap-3 rounded-lg border border-primary bg-white p-4 mt-2">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>First name</Label>
          <Input
            placeholder="First name"
            value={addFirstName}
            onChange={(e) => setAddFirstName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Last name</Label>
          <Input
            placeholder="Last name"
            value={addLastName}
            onChange={(e) => setAddLastName(e.target.value)}
          />
        </div>
      </div>
      {!isWitnessSection && (
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Relationship</Label>
            <Select value={addRelationship} onValueChange={setAddRelationship}>
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Status</Label>
            <Select value={addStatus} onValueChange={setAddStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="living">Living</SelectItem>
                <SelectItem value="passed_away">Passed away</SelectItem>
                <SelectItem value="not_sure">Not sure</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={cancelAdd}>Cancel</Button>
        <Button
          variant="brand"
          size="sm"
          onClick={saveAdd}
          disabled={!addFirstName.trim() || !addLastName.trim()}
        >
          Add
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Let's make sure we have the facts right
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          The court checks this list to determine who needs to sign or receive
          notice. A quick check now prevents filing delays later. You'll have
          time to update anything you're unsure of before filing.
        </p>
        {hasWill && (
          <p className="mt-1 text-[13px] italic text-secondary">
            Names from {name}'s will are already included.
          </p>
        )}
      </div>

      {/* People section */}
      <div className="flex flex-col gap-2">
        <p className="text-base font-semibold tracking-[-0.32px]">People</p>
        {people.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-primary">
            {people.map((contact, i) =>
              editingId === contact.id ? (
                renderEditRow(contact)
              ) : (
                <div
                  key={contact.id}
                  className={`flex items-center gap-4 bg-white p-4 ${i > 0 ? "border-t border-primary" : ""}`}
                >
                  <div className="flex flex-1 flex-col min-w-0">
                    <div className="flex items-start gap-2">
                      <p className="text-base font-semibold tracking-[-0.32px]">
                        {formatContactName(contact.first_name, contact.last_name, contact.middle_name)}
                      </p>
                      {isNamedInWill(contact) && (
                        <span className="shrink-0 rounded-full border border-stone-300 bg-stone-50 px-2.5 py-0.5 text-sm font-medium text-stone-700 tracking-[-0.28px]">
                          Named in will
                        </span>
                      )}
                      {isDualWitness(contact) && (
                        <span className="shrink-0 rounded-full border border-stone-300 bg-stone-50 px-2.5 py-0.5 text-sm font-medium text-stone-700 tracking-[-0.28px]">
                          Will witness
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {contact.relationship && (
                        <p className="text-sm text-secondary tracking-[-0.28px]">
                          {getRelationshipLabel(contact.relationship)}
                        </p>
                      )}
                      {contact.relationship && contact.person_status && <DotSeparator />}
                      {contact.person_status && (
                        <p className="text-sm text-secondary tracking-[-0.28px]">
                          {getStatusLabel(contact.person_status)}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => startEdit(contact)}
                    className="shrink-0 p-1 text-tertiary hover:text-primary"
                    aria-label={`Edit ${contact.first_name}`}
                  >
                    <Pencil className="size-5" />
                  </button>
                </div>
              ),
            )}
          </div>
        )}
        {addingSection === "people" ? (
          renderAddRow(false)
        ) : (
          <Button variant="link" className="text-secondary w-fit p-0" onClick={() => startAdd("people")}>
            Is someone missing? Add them
          </Button>
        )}
      </div>

      {/* Organizations section */}
      {organizations.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-base font-semibold tracking-[-0.32px]">Organizations</p>
          <div className="overflow-hidden rounded-lg border border-primary">
            {organizations.map((org, i) => (
              <div
                key={org.organization_name}
                className={`flex items-center gap-4 bg-white p-4 ${i > 0 ? "border-t border-primary" : ""}`}
              >
                <div className="flex flex-1 items-start gap-2 min-w-0">
                  <p className="text-base font-semibold tracking-[-0.32px]">
                    {org.organization_name}
                  </p>
                  <span className="shrink-0 rounded-full border border-stone-300 bg-stone-50 px-2.5 py-0.5 text-sm font-medium text-stone-700 tracking-[-0.28px]">
                    Named in will
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Will witnesses section */}
      <div className="flex flex-col gap-2">
        <p className="text-base font-semibold tracking-[-0.32px]">Will witnesses</p>
        {witnesses.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-primary">
            {witnesses.map((contact, i) =>
              editingId === contact.id ? (
                renderEditRow(contact, true)
              ) : (
                <div
                  key={contact.id}
                  className={`flex items-center gap-4 bg-white p-4 ${i > 0 ? "border-t border-primary" : ""}`}
                >
                  <p className="flex-1 text-base font-semibold tracking-[-0.32px] min-w-0">
                    {formatContactName(contact.first_name, contact.last_name, contact.middle_name)}
                  </p>
                  <button
                    type="button"
                    onClick={() => startEdit(contact)}
                    className="shrink-0 p-1 text-tertiary hover:text-primary"
                    aria-label={`Edit ${contact.first_name}`}
                  >
                    <Pencil className="size-5" />
                  </button>
                </div>
              ),
            )}
          </div>
        )}
        {addingSection === "witness" ? (
          renderAddRow(true)
        ) : (
          <Button variant="link" className="text-secondary w-fit p-0" onClick={() => startAdd("witness")}>
            Is a witness missing? Add them
          </Button>
        )}
      </div>
    </div>
  );
};
